import * as React from "react";
import { useDropdownAlignment } from "../../../hooks/useDropdownAlignment";
import styled from "styled-components";
import BodySmall from "../typography/BodySmall";
import { Row } from "react-grid-system";
import { theme } from "../../../theme";
import {
  ChevronSmallDownIcon,
  ChevronSmallUpIcon,
} from "../icons/ChevronIcons";
import { HorizontalSpacer, VerticalSpacer } from "../Spacers";
import { useTranslation } from "react-i18next";
import TextField from "./TextField";
import Divider from "../Divider";
import DefaultButton from "../buttons/DefaultButton";
import ActionButton from "../buttons/ActionButton";
import Body from "../typography/Body";
import RangeSlider from "./RangeSlider";

interface Props {
  minValue?: number;
  maxValue?: number;
  selectedMin?: number;
  selectedMax?: number;
  onChange: (range: { minValue?: number; maxValue?: number }) => void;
  title: string;
}

const SelectContainer = styled.div`
  border-bottom: 1.25px solid ${theme.colors.grey40};
  padding: 0px 8px;
  height: 36px;
  box-sizing: border-box;
  color: ${theme.colors.text};
  font-size: 16px;
  display: flex;
  flex-direction: row;
  align-content: center;
  width: fit-content;
  gap: 8px;
  background-color: ${theme.colors.white};

  &:hover {
    cursor: pointer;
    background-color: ${theme.colors.hover};
  }
`;

const RangeContainer = styled.div<{ $alignRight: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  ${({ $alignRight }) => ($alignRight ? "right: 0;" : "left: 0;")}
  z-index: 100;
  background-color: ${theme.colors.white};
  border-radius: 4px;
  border: 1.25px solid ${theme.colors.grey40};
  padding: 16px;
  width: fit-content;
  box-shadow: ${theme.shadow.cardShadow};
`;

const AmountInputContainer = styled.div`
  display: flex;
  gap: 24px;
  padding: 8px;
`;

const FlexEndContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 24px;
`;

const RangeSelect: React.FC<Props> = ({
  onChange,
  title,
  minValue,
  maxValue,
  selectedMax,
  selectedMin,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const { panelRef, alignRight } = useDropdownAlignment(open);

  const [min, setMin] = React.useState<number | undefined>(selectedMin);
  const [max, setMax] = React.useState<number | undefined>(selectedMax);

  const dropdownRef = React.useRef<HTMLDivElement | null>(null);

  const handleClickOutside = (event: any) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div style={{ position: "relative" }} ref={dropdownRef}>
      <SelectContainer>
        <Row
          justify="between"
          nogutter
          align="center"
          onClick={() => setOpen(!open)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <BodySmall fontweight={600}>{title}:</BodySmall>
            <BodySmall>
              {selectedMin === undefined && selectedMax === undefined
                ? t("common.any")
                : `
              ${
                selectedMin !== undefined ? selectedMin : t("common.any")
              } -${" "}
              ${selectedMax !== undefined ? selectedMax : t("common.any")}`}
            </BodySmall>
          </div>
          <VerticalSpacer spacing={0.5} />
          {open ? <ChevronSmallUpIcon /> : <ChevronSmallDownIcon />}
        </Row>
        {open && (
          <RangeContainer ref={panelRef} $alignRight={alignRight}>
            <Body>{t("invoices.selectRange")}</Body>
            <AmountInputContainer>
              <TextField
                label={t("invoices.from")}
                type="number"
                value={min}
                onChange={(v) => {
                  const value = v === "" ? undefined : parseFloat(v);
                  setMin(value);
                }}
                minValue={minValue}
                width="180px"
              />
              <TextField
                label={t("invoices.to")}
                type="number"
                value={max}
                onChange={(v) => {
                  const value = v === "" ? undefined : parseFloat(v);
                  setMax(value);
                }}
                maxValue={maxValue}
                width="180px"
              />
            </AmountInputContainer>

            <HorizontalSpacer spacing={1} />
            <RangeSlider
              min={minValue ?? -10000}
              max={maxValue ?? 1000000}
              fromValue={min ?? minValue ?? -10000}
              toValue={max ?? maxValue ?? 1000000}
              onChangeFromValue={(v) => setMin(v)}
              onChangeToValue={(v) => setMax(v)}
            />

            <HorizontalSpacer spacing={2} />
            <Divider />
            <HorizontalSpacer spacing={1} />
            <FlexEndContainer>
              <DefaultButton
                title={t("common.cancel")}
                onClick={() => setOpen(false)}
              />
              <ActionButton
                color={theme.colors.primary}
                title={t("common.select")}
                onClick={() => {
                  onChange({ minValue: min, maxValue: max });
                  setOpen(false);
                }}
              />
            </FlexEndContainer>
          </RangeContainer>
        )}
      </SelectContainer>
    </div>
  );
};

export default RangeSelect;
